import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IBlueprintsDAO, IEventBlueprintDTO, IMeetingTemplateDTO } from '../../../api/blueprints/blueprints.dao';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { IntlShape } from 'react-intl';
import { EventBlueprintForm } from './event-blueprint-form/event-blueprint-form';
import { IBlueprintItemDetails } from '../../../api/blueprints/blueprint-details.types';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../infrastructure/utils/blueprint-details.mapper';
import { GlobalViewModel } from '../../global.viewmodel';
import { TEventTypeLabel } from '../../../api/events.dao';


export class EventBlueprintsVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    public isLoading: boolean = false;
    @observable
    private blueprints: IEventBlueprintDTO[] = [];
    @observable
    private blueprintIdToEditPopupMap: { [key: string]: ISubWindowFacade } = {};
    @observable
    public selectedTemplate: IBlueprintItemDetails;
    private meetingTemplates: IMeetingTemplateDTO[] = [];

    constructor(
        private readonly accountId: number,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly globalViewModel: GlobalViewModel = IoC.Container.get<GlobalViewModel>(GlobalViewModel),
    ) { }

    public get eventBlueprints(): IEventBlueprintDTO[] {
        return this.blueprints;
    }

    @action.bound
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const params = { accountId: this.accountId };

            const [ blueprintsResponse, templatesResponse ] = await Promise.all([
                this.blueprintsDAO.getEventBlueprintList(params),
                this.blueprintsDAO.getMeetingTemplateList(params)
            ]);

            this.setBlueprints(blueprintsResponse);
            this.meetingTemplates = templatesResponse;
        } catch (error) {
            EventBlueprintsVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public openCreateBlueprintForm(): void {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.blueprints.event.create.title',
                defaultMessage: 'New Event Blueprint',
                description: 'Popover header text'
            }),
            componentToHost: EventBlueprintForm,
            componentToHostProps: {
                accountId: this.accountId,
                intl: this.intl,
                eventTypesUsedInAccount: this.getEventTypesUsedInAccount(),
                apiCallSuccessCallback: () => {
                    this.initialize();
                }
            }
        });
    }

    public openEditBlueprintForm(blueprintId: string): ISubWindowFacade {
        return this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.blueprints.event.edit.title',
                defaultMessage: 'Edit Event Blueprint',
                description: 'Popover header text'
            }),
            componentToHost: EventBlueprintForm,
            componentToHostProps: {
                accountId: this.accountId,
                intl: this.intl,
                eventTypesUsedInAccount: this.getEventTypesUsedInAccount(blueprintId),
                blueprintId: blueprintId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
                deleteCompletionCallback: () => this.completeEventBluePrintDeletion(blueprintId)
            }
        });
    }

    public getEventTypesUsedInAccount(excludedBlueprintId?: string): TEventTypeLabel[] {
        return this.blueprints.reduce((_eventTypes, blueprint) => {
            return excludedBlueprintId && blueprint.blueprintId === excludedBlueprintId
                ? _eventTypes
                : _eventTypes.concat(blueprint.eventTypes)
            ;
        }, [] as TEventTypeLabel[]);
    }

    @action
    public editBlueprint = (blueprintId: string) => {
        if (this.isEditFormHidden(blueprintId)) {
            this.blueprintIdToEditPopupMap[blueprintId] = this.openEditBlueprintForm(blueprintId);
        }
    };

    public isEditFormHidden = (blueprintId: string) => {
        const instance = this.blueprintIdToEditPopupMap[blueprintId];
        return !instance || instance.getStatus() === SubWindowStatus.Closed;
    };

    private completeEventBluePrintDeletion = (deletedBlueprintId: string): void => {
        this.closeEditForm(deletedBlueprintId);
        this.initialize();
    };

    private closeEditForm(blueprintId: string): void {
        this.blueprintIdToEditPopupMap[blueprintId].close();
    }

    @action.bound
    private setBlueprints(eventBlueprints: IEventBlueprintDTO[]): void {
        this.blueprints = eventBlueprints
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    @action.bound
    private startLoading(): void {
        this.isLoading = true;
    }

    @action.bound
    private stopLoading(): void {
        this.isLoading = false;
    }

    public isTemplateActive = (blueprintId: string): boolean => {
        return this.selectedTemplate?.blueprintId === blueprintId;
    };

    @action
    public openEventTemplateDetails = (blueprintId: string): void => {
        const template = this.blueprints.find(t => t.blueprintId === blueprintId);
        this.selectedTemplate = convertBlueprintTemplateIntoBlueprintDetails(
            template.type,
            template,
            this.globalViewModel.getTimezonesVM().getTimezonesList(),
            this.meetingTemplates
        );
    };

    @action
    public closeBluePrintItemDetailsPopover = (): void => {
        this.selectedTemplate = undefined;
    };

    public isTemplatSelected = (blueprintId: string): boolean => {
        return !this.isEditFormHidden(blueprintId) || this.isTemplateActive(blueprintId);
    };

    public get globalVm(): GlobalViewModel {
        return this.globalViewModel;
    }
}
